import { create } from 'zustand';
import type { AdminQuestsState, QuestClaimResult, QuestState, QuestTodayResponse } from '@tg/shared';
import { api, type RequestOptions } from '@/api/client';

/** REST of apps/server/src/quests (contracts in @tg/shared quests.ts). */
export const questApi = {
  today: (o?: RequestOptions) => api.get<QuestTodayResponse>('/api/quests/today', o),
  claim: () => api.post<QuestClaimResult>('/api/quests/today/claim'),
  admin: () => api.get<AdminQuestsState>('/api/admin/quests'),
};

/**
 * The daily quest of the signed-in player: GET /api/quests/today on sign-in and after the UTC midnight, kept live by
 * the `quest:progress` socket event (QuestListener). `quest` null — a guest, not loaded yet, or switched off.
 */
interface QuestStore {
  quest: QuestState | null;
  claiming: boolean;
  load(o?: RequestOptions): Promise<void>;
  /** a fresh state (socket push or claim); returns the previous one */
  set(q: QuestState): QuestState | null;
  claim(): Promise<QuestClaimResult>;
  reset(): void;
}

export const useQuest = create<QuestStore>()((set, get) => ({
  quest: null,
  claiming: false,
  async load(o) {
    try {
      const r = await questApi.today(o);
      set({ quest: r.quest });
    } catch {
      /* keep what we have: the quest never blocks a page */
    }
  },
  set(q) {
    const prev = get().quest;
    set({ quest: q });
    return prev;
  },
  async claim() {
    if (get().claiming) throw new Error('busy');
    set({ claiming: true });
    try {
      const r = await questApi.claim();
      set({ quest: r.state });
      return r;
    } finally {
      set({ claiming: false });
    }
  },
  reset: () => set({ quest: null, claiming: false }),
}));
